import { FileCode, Eye, CheckCircle, XCircle, User, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import FormattedDate from '@/components/common/FormattedDate';
import type { WorkflowTemplate, WorkflowEnvironment } from '@/lib/types';

interface Props {
  templates: WorkflowTemplate[];
  loading: boolean;
  onSelectTemplate: (template: WorkflowTemplate) => void;
}

const envBadgeStyles: Record<WorkflowEnvironment, string> = {
  production: 'bg-green-500/20 text-green-400 border-green-500/30',
  staging: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

export default function WorkflowTemplatesTable({ templates, loading, onSelectTemplate }: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FileCode className="h-12 w-12 mb-4 opacity-50" />
        <p>No workflow templates found</p>
        <p className="text-sm mt-1">Create a new template to get started</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Subtype
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Version
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Environment
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Scope</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Variables
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Status
            </th>
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              Created
            </th>
            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => (
            <tr
              key={template.id}
              className={cn(
                'border-b border-border/50 hover:bg-muted/20 transition-colors',
                template.disable_ts && 'opacity-50'
              )}
            >
              <td className="py-3 px-4">
                <span className="font-mono text-sm">{template.type_name}</span>
              </td>
              <td className="py-3 px-4">
                <span className="font-mono text-sm">{template.subtype}</span>
              </td>
              <td className="py-3 px-4">
                <span className="font-mono text-sm text-muted-foreground">{template.version}</span>
              </td>
              <td className="py-3 px-4">
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
                    envBadgeStyles[template.environment]
                  )}
                >
                  {template.environment}
                </span>
              </td>
              <td className="py-3 px-4">
                {template.customer_id ? (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="h-3 w-3" />
                    Customer
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <Globe className="h-3 w-3" />
                    Global
                  </span>
                )}
              </td>
              <td className="py-3 px-4">
                <span className="text-sm text-muted-foreground">
                  {template.variables?.length || 0}
                </span>
              </td>
              <td className="py-3 px-4">
                {template.disable_ts ? (
                  <span className="inline-flex items-center gap-1 text-red-400">
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">Disabled</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Active</span>
                  </span>
                )}
              </td>
              <td className="py-3 px-4">
                <FormattedDate date={template.create_ts} />
              </td>
              <td className="py-3 px-4 text-right">
                <button
                  type="button"
                  onClick={() => onSelectTemplate(template)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm"
                >
                  <Eye className="h-3.5 w-3.5" />
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
